/**
 * Property directory helpers.
 *
 * `property_directory_customer_view` is the customer-safe projection of the
 * managed-property registry (navigation ids for Mission Control / UniFi /
 * Auvik plus Learn manuals). Locations link to it through
 * `locations.hierarchy->>'property_code'`. Everything here is pure; the
 * URL builders themselves live in `@pazportal/ui` (propertyLinks) so admin
 * shares them.
 */

import {
  directoryLinks as sharedDirectoryLinks,
  parseAdditionalNetworks,
  type AdditionalNetwork,
  type PropertyLinks,
} from '@pazportal/ui';

export const PROPERTY_DIRECTORY_COLUMNS =
  'code, name, brand, city, state, lat, lng, rankings_url, mission_control_site_id, unifi_host_id, auvik_domain_prefix, learn_url, engineering_url, ordering_url, communications_vendor_url, additional_networks, updated_at, aliases, visit_history_note';

export interface PropertyDirectoryEntry {
  code: string;
  name: string;
  brand: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lng: number | null;
  rankings_url: string | null;
  mission_control_site_id: string | null;
  unifi_host_id: string | null;
  auvik_domain_prefix: string | null;
  learn_url: string | null;
  engineering_url: string | null;
  ordering_url: string | null;
  communications_vendor_url: string | null;
  additional_networks: AdditionalNetwork[];
  updated_at: string | null;
  /** Alternative codes and names operations uses for this property. */
  aliases: string[];
  visit_history_note: string | null;
}

export { parseAdditionalNetworks, missionControlUrl, unifiConsoleUrl, auvikUrl, type AdditionalNetwork, type PropertyLinks } from '@pazportal/ui';

/** Raw row shape as PostgREST returns it (every column nullable, JSON untyped). */
export interface PropertyDirectoryRow {
  code: string | null;
  name: string | null;
  brand: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lng: number | null;
  rankings_url: string | null;
  mission_control_site_id: string | null;
  unifi_host_id: string | null;
  auvik_domain_prefix: string | null;
  learn_url: string | null;
  engineering_url: string | null;
  ordering_url: string | null;
  communications_vendor_url: string | null;
  additional_networks: unknown;
  updated_at: string | null;
  aliases: unknown;
  visit_history_note: string | null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

export function parseAliases(json: unknown): string[] {
  if (!Array.isArray(json)) return [];
  const out: string[] = [];
  for (const item of json) {
    const alias = stringOrNull(item);
    if (alias) out.push(alias);
  }
  return out;
}

export function toDirectoryEntry(row: PropertyDirectoryRow): PropertyDirectoryEntry | null {
  if (!row.code || !row.name) return null;
  return {
    code: row.code,
    name: row.name,
    brand: row.brand,
    city: row.city,
    state: row.state,
    lat: typeof row.lat === 'number' && Number.isFinite(row.lat) ? row.lat : null,
    lng: typeof row.lng === 'number' && Number.isFinite(row.lng) ? row.lng : null,
    rankings_url: row.rankings_url,
    mission_control_site_id: row.mission_control_site_id,
    unifi_host_id: row.unifi_host_id,
    auvik_domain_prefix: row.auvik_domain_prefix,
    learn_url: row.learn_url,
    engineering_url: row.engineering_url,
    ordering_url: row.ordering_url,
    communications_vendor_url: row.communications_vendor_url,
    additional_networks: parseAdditionalNetworks(row.additional_networks),
    updated_at: row.updated_at,
    aliases: parseAliases(row.aliases),
    visit_history_note: stringOrNull(row.visit_history_note),
  };
}

/** Site-resource URLs for a directory entry (see `@pazportal/ui` propertyLinks). */
export function directoryLinks(entry: PropertyDirectoryEntry | null): PropertyLinks {
  return sharedDirectoryLinks(entry);
}
